import { TTypeOfCodec } from "../../shared/src/codecs/codec";
import { required } from "../../shared/src/codecs/types/required";
import { union } from "../../shared/src/codecs/types/union";
import { nullCodec } from "../../shared/src/codecs/types/nullCodec";
import { string } from "../../shared/src/codecs/types/string";
import { boolean } from "../../shared/src/codecs/types/boolean";
import { uuid } from "../../shared/src/codecs/types/uuid";
import { dateTime } from "../../shared/src/codecs/types/dateTime";
import {
    UsersIdCheckIdentityDocumentStatus,
    UsersIdCheckAddressStatus,
    UsersIdCheckPepsSanctionsStatus,
} from "./UsersIdCheck";

// One seller on a listing, with the AML evidence we can derive for them — or nothing.
//
// Every evidence field is nullable as a block: either the derivation found a check for
// this person or it did not. A seller with no email, no linked case, or a linked case
// whose clients are different people all arrive here the same way, as an absence.
//
// That absence is the point. The state this replaces was
// `users.homes_id_or_gop_seen_and_confirmed_good` — a bare staff tick carrying no actor,
// no document and no expiry — so a renderer that shows "unevidenced" as a blank or a
// neutral tick reproduces the exact defect. It must read as a gap.
export const VendorAmlEvidence = required({
    user_id: uuid(),
    seller_name: string(),

    // Nullable because `users.is_legal_seller` is, and the distinction matters: a party
    // member who is not a legal seller is not selling, so CDD is not owed on them. This is
    // also why the two Sail Homes chaser jobs are staged — establish the legal seller
    // first, then check that person's ID.
    is_legal_seller: union([boolean(), nullCodec()]),

    id_check_id: union([uuid(), nullCodec()]),
    identity_status: union([UsersIdCheckIdentityDocumentStatus, nullCodec()]),
    address_status: union([UsersIdCheckAddressStatus, nullCodec()]),
    peps_sanctions_status: union([UsersIdCheckPepsSanctionsStatus, nullCodec()]),
    checked_at: union([dateTime(), nullCodec()]),

    // The 12-month screening clock, not the ID document's own expiry. What lapses is the
    // PEPs/sanctions and address screening, which genuinely decays; the identity proof
    // does not. An expired check is still evidence that a check happened — it is shown,
    // flagged, and never silently dropped.
    expires_at: union([dateTime(), nullCodec()]),
    has_expired: union([boolean(), nullCodec()]),

    // Identity established by either route — an automated Stripe pass or a recorded
    // `manual_pass` sign-off. Reading `= 'pass'` by hand instead is what let manual_pass
    // clients keep receiving the ID-check chaser for five weeks.
    is_established: union([boolean(), nullCodec()]),

    // The case whose membership identified this human. Provenance the CRM must show:
    // staff can never be left unable to answer "where did this come from?".
    via_case_id: union([uuid(), nullCodec()]),

    // The case that commissioned the check, which may be an earlier matter — a check
    // follows the person across cases, so the two are often different.
    check_created_for_case_id: union([uuid(), nullCodec()]),
});
export type TVendorAmlEvidenceCodec = typeof VendorAmlEvidence;
export type TVendorAmlEvidence = TTypeOfCodec<TVendorAmlEvidenceCodec>;
